import { useController, Control, useFieldArray } from 'react-hook-form';
import {
  createMaxLengthValidator,
  createMinLengthValidator,
  createRequiredValidator,
  isRequired,
  isValidInput,
  isOptionRequired,
} from '../../validators';
import { ProgramRegistrationFormData } from './types';

export const useUseCaseController = (
  control: Control<ProgramRegistrationFormData>
) => {
  const {
    field: { value: brandUid, onChange: setBrandUid },
    fieldState: { error: brandUidError },
  } = useController({
    name: 'usecase.brandUid',
    control,
    rules: { validate: { isRequired } },
  });

  const {
    field: { value: brandName, onChange: setBrandName },
    fieldState: { error: brandNameError },
  } = useController({
    name: 'usecase.brandName',
    control,
    rules: { validate: { isRequired } },
  });

  const {
    field: { value: usecase, onChange: setUsecase },
    fieldState: { error: usecaseError },
  } = useController({
    name: 'usecase.usecase',
    control,
    rules: { validate: { isRequired } },
  });

  return {
    brandUid,
    setBrandUid,
    brandUidError,
    brandName,
    setBrandName,
    brandNameError,
    usecase,
    setUsecase,
    usecaseError,
  };
};

export const useRoutingController = (
  control: Control<ProgramRegistrationFormData>
) => {
  const {
    field: { value: routingMode, onChange: setRoutingMode },
    fieldState: { error: routingModeError },
  } = useController({
    name: 'routing.routingMode',
    control,
    rules: { validate: { isRequired } },
  });

  const {
    field: { value: offNetOutbound, onChange: setOffNetOutbound },
    fieldState: { error: offNetOutboundError },
  } = useController({
    name: 'routing.offNetOutboundTerminationSupported',
    control,
    rules: {
      validate: isOptionRequired,
    },
  });

  const {
    field: { value: intlInbound, onChange: setIntlInbound },
    fieldState: { error: intlInboundError },
  } = useController({
    name: 'routing.intlInboundTerminationSupported',
    control,
    rules: {
      validate: isOptionRequired,
    },
  });

  const {
    field: { value: intlOutbound, onChange: setIntlOutbound },
    fieldState: { error: intlOutboundError },
  } = useController({
    name: 'routing.intlOutboundTerminationSupported',
    control,
    rules: {
      validate: isOptionRequired,
    },
  });

  const {
    field: { value: onNetInbound, onChange: setOnNetInbound },
    fieldState: { error: onNetInboundError },
  } = useController({
    name: 'routing.onNetInboundTerminationSupported',
    control,
    rules: {
      validate: isOptionRequired,
    },
  });

  const {
    field: { value: offNetInbound, onChange: setOffNetInbound },
    fieldState: { error: offNetInboundError },
  } = useController({
    name: 'routing.offNetInboundTerminationSupported',
    control,
    rules: {
      validate: isOptionRequired,
    },
  });

  const {
    field: { value: upstreamCnpUid, onChange: setUpstreamCnpUid },
    fieldState: { error: upstreamCnpUidError },
  } = useController({
    name: 'routing.upstreamCnpUid',
    control,
    rules: {
      validate: {
        isRequired: createRequiredValidator(
          'Please select a connectivity partner'
        ),
      },
    },
  });

  const {
    field: { value: mnoIds, onChange: setMnoIds },
    fieldState: { error: mnoIdsError },
  } = useController({
    name: 'routing.mnoIds',
    control,
    rules: {
      validate: (value) =>
        (value && value.length > 0) || 'Please select at least one MNO',
    },
  });

  return {
    routingMode,
    setRoutingMode,
    routingModeError,
    offNetOutbound,
    setOffNetOutbound,
    offNetOutboundError,
    intlInbound,
    setIntlInbound,
    intlInboundError,
    intlOutbound,
    setIntlOutbound,
    intlOutboundError,
    onNetInbound,
    setOnNetInbound,
    onNetInboundError,
    offNetInbound,
    setOffNetInbound,
    offNetInboundError,
    upstreamCnpUid,
    setUpstreamCnpUid,
    upstreamCnpUidError,
    mnoIds,
    setMnoIds,
    mnoIdsError,
  };
};

export const useDetailsController = (
  control: Control<ProgramRegistrationFormData>
) => {
  const {
    field: {
      value: description,
      onChange: setDescription,
      onBlur: validateDescription,
    },
    fieldState: { error: descriptionError },
  } = useController({
    name: 'details.description',
    control,
    rules: {
      validate: {
        isValidInput,
        isRequired,
        withValidMaxLength: createMaxLengthValidator(4096),
        withValidMinLength: createMinLengthValidator(40),
      },
    },
  });

  const {
    field: {
      value: referenceId,
      onChange: setReferenceId,
      onBlur: validateReferenceId,
    },
    fieldState: { error: referenceIdError },
  } = useController({
    name: 'details.referenceId',
    control,
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(50),
      },
    },
  });

  const {
    field: {
      value: expectedMessageVolume,
      onChange: setExpectedMessageVolume,
      onBlur: validateExpectedMessageVolume,
    },
    fieldState: { error: expectedMessageVolumeError },
  } = useController({
    name: 'details.expectedMessageVolume',
    control,
    rules: {
      validate: {
        isRequired: (value) => value !== '' || 'Required',
        isNumber: (value) =>
          value === '' ||
          /^[1-9]\d*$/.test(value) ||
          'Must be a valid positive integer',
        // Ensure value does not exceed Java's Integer.MAX_VALUE to prevent backend deserialization errors
        isWithinMaxLimit: (value) =>
          value === '' ||
          (/^[1-9]\d*$/.test(value) && Number(value) <= 2147483647) ||
          'Exceeds maximum allowed volume (2,147,483,647)',
      },
    },
  });

  const {
    field: {
      value: supportingDocumentUuid,
      onChange: setSupportingDocumentUuid,
    },
    fieldState: { error: supportingDocumentUuidError },
  } = useController({
    name: 'details.supportingDocumentUuid',
    control,
  });

  const sampleMessagesArray = useFieldArray({
    control,
    name: 'details.sampleMessages',
  });

  const {
    field: { value: embeddedLink, onChange: setEmbeddedLink },
    fieldState: { error: embeddedLinkError },
  } = useController({
    name: 'details.embeddedLink',
    control,
    rules: { validate: { isOptionRequired } },
  });

  const {
    field: {
      value: embeddedLinkSample,
      onChange: setEmbeddedLinkSample,
      onBlur: validateEmbeddedLinkSample,
    },
    fieldState: { error: embeddedLinkSampleError },
  } = useController({
    name: 'details.embeddedLinkSample',
    disabled: !embeddedLink,
    control,
    rules: {
      validate: {
        isValidInput,
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });

  const {
    field: { value: embeddedPhone, onChange: setEmbeddedPhone },
    fieldState: { error: embeddedPhoneError },
  } = useController({
    name: 'details.embeddedPhone',
    control,
    rules: { validate: { isOptionRequired } },
  });

  return {
    description,
    setDescription,
    descriptionError,
    validateDescription,
    referenceId,
    setReferenceId,
    referenceIdError,
    validateReferenceId,
    expectedMessageVolume,
    setExpectedMessageVolume,
    expectedMessageVolumeError,
    validateExpectedMessageVolume,
    supportingDocumentUuid,
    setSupportingDocumentUuid,
    supportingDocumentUuidError,
    sampleMessagesArray,
    embeddedLink,
    setEmbeddedLink,
    embeddedLinkError,
    embeddedLinkSample,
    setEmbeddedLinkSample,
    validateEmbeddedLinkSample,
    embeddedLinkSampleError,
    embeddedPhone,
    setEmbeddedPhone,
    embeddedPhoneError,
  };
};
